import type {
  JetStreamClient,
  JetStreamManager,
  JetStreamOptions,
  Msg,
  NatsConnection,
  PublishOptions,
  QueuedIterator,
  RequestManyOptions,
  RequestOptions,
  ServicesAPI,
  Stats,
  Status,
  Subscription,
  SubscriptionOptions
} from 'nats.ws';
import { Router } from './router.js';
import { type RemoteParticipant, RoomEvent, type DataPublishOptions } from 'livekit-client';
import {
  AgentDirectNewtsMessage,
  AgentDirectTrackTimestamp
} from '@tryiris-ai/common.protojs/api/v1/agent_direct';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { LiveKitImpl } from '#~/livekit/livekit.js';
import { logger } from '#~/logger/index.js';
import { SharedTimestampManager } from '#~/auto-tracking/provider/timestamp-manager.js';
import { toIdFromHex } from '#~/api/utils.js';

const router = new Router();

// Maximum number of messages to buffer when device is offline (prevents unbounded memory growth)
const MAX_MSG_BUFFER_SIZE = 100;
// Maximum total bytes in message buffer (10MB limit)
const MAX_MSG_BUFFER_BYTES = 10 * 1024 * 1024;

export class NewtsConnection implements NatsConnection {
  #livekit: T_Maybe<LiveKitImpl>;
  #router: Router;
  #agentId: string;

  #msgBuffer = new Set<Uint8Array>();
  #msgBufferTotalBytes = 0;
  #sendBufferInFlight: Promise<void> | undefined;
  #reconnectFlushInFlight: Promise<void> | undefined;

  constructor(agentId: string) {
    this.#router = router;
    this.#agentId = agentId;

    this.#router.onPublishMap[this.#agentId] = async (msg) => {
      this.#addToMsgBuffer(msg);
    };
  }

  /**
   * Adds a message to the buffer with size limits to prevent memory leaks.
   * When limits are exceeded, oldest messages are dropped.
   */
  #addToMsgBuffer(msg: Uint8Array): void {
    // Check if adding this message would exceed limits
    if (this.#msgBuffer.size >= MAX_MSG_BUFFER_SIZE) {
      // Remove oldest message (first in set)
      const oldest = this.#msgBuffer.values().next().value;
      if (oldest) {
        this.#msgBuffer.delete(oldest);
        this.#msgBufferTotalBytes -= oldest.length;
      }
    }

    // Also check byte limit
    while (
      this.#msgBufferTotalBytes + msg.length > MAX_MSG_BUFFER_BYTES &&
      this.#msgBuffer.size > 0
    ) {
      const oldest = this.#msgBuffer.values().next().value;
      if (oldest) {
        this.#msgBuffer.delete(oldest);
        this.#msgBufferTotalBytes -= oldest.length;
      } else {
        break;
      }
    }

    this.#msgBuffer.add(msg);
    this.#msgBufferTotalBytes += msg.length;
  }

  /**
   * Clears the message buffer and resets byte counter.
   * Call this when cleaning up a disconnected device.
   */
  public clearMsgBuffer(): void {
    this.#msgBuffer.clear();
    this.#msgBufferTotalBytes = 0;
  }

  init(livekit: LiveKitImpl) {
    /*if(this.#livekit) {
      return;
    }*/

    this.#livekit = livekit;

    this.#router.onPublishMap[this.#agentId] = async (
      msg,
      replyTo: T_Maybe<string>,
      unsafeOptions
    ) => {
      const options: {
        mode?: 'local' | 'cloud' | 'hybrid';
        lossy?: boolean;
        subject?: string;
      } = this.#livekit?.embedded ? { ...unsafeOptions, mode: 'hybrid' } : (unsafeOptions ?? {});

      options.mode = 'hybrid';

      // Gate on room connection state, not participant object presence —
      // a closed room still has a localParticipant and selecting it throws
      // "PC manager is closed".
      if (!this.isConnected()) {
        if (replyTo) {
          // Reply-bearing: fail the waiter now. Do not buffer a request
          // that can never complete on a dead PC.
          throw new Error('transport disconnected');
        }

        this.#addToMsgBuffer(msg);
        return;
      }

      // S6: presence is OR over both rooms. inRoom already does this;
      // remoteParticipantsWithOptions followed connectedMode and self-rejected
      // requests when the agent was only on the other live transport.
      const isAgentPresent =
        this.#livekit?.inRoom?.(toIdFromHex(this.#agentId)!, this.#agentId) ?? false;

      // Ping bypass (race fix 2026-08-25): pings are the PROBE that decides
      // bridge online/offline — they must be sent even when the presence
      // snapshot says the agent is absent, or a transient dual-room blip
      // instantly rejects the ping and the UI flips offline for the ~10s
      // until the next presence event ("offline only when pings fail"
      // contract). The reject stays for every other reply-bearing request,
      // where fast-fail beats a hung command.
      const isPing = !!options?.subject?.endsWith('.ping');

      if (!isAgentPresent && replyTo && !isPing) {
        // WAN-off diagnosis 2026-08-25: this instant-reject is the visible
        // failure when routing is wrong — log which subject died here.
        logger.warning(
          `newts request rejected: agent not present in mode-room subject=${options?.subject ?? '?'}`
        );
        this.publish(replyTo, new Uint8Array(0), { reject: true });
        return;
      }

      const reliable = !(options?.lossy === true);

      let destinationIdentities: string[] | undefined = [this.#agentId];

      if (options?.subject?.startsWith('$direct.peer.')) {
        const identity = options.subject.split('.')[2];
        destinationIdentities = [identity!];
      }

      if (options?.subject?.startsWith('$direct.broadcast.')) {
        destinationIdentities = undefined;
      }

      const opts: DataPublishOptions = {
        reliable
      };

      if (!!destinationIdentities) {
        opts.destinationIdentities = destinationIdentities;
      }

      try {
        // Reply-bearing requests are one-shot control operations. Send the same
        // encoded message through local and cloud so a local-room replacement
        // cannot silently discard it; the device and Studio routers' message-id
        // guards deduplicate whichever request/response copy arrives second.
        // Fire-and-forget traffic stays on the selected route to avoid doubling
        // the regular data stream.
        // Per rule: architecture-principles (ai.knowledge/global) — preserve
        // Electron/browser parity while keeping transport concerns modular.
        await this.#livekit?.publishDataWithLocalParticipant(
          toIdFromHex(this.#agentId)!,
          { mode: replyTo ? 'hybrid' : 'auto' },
          msg,
          opts
        );
      } catch (e) {
        if (!replyTo) {
          this.#addToMsgBuffer(msg);
          return;
        }

        throw e;
      }
    };

    this.#livekit.onRoomEvent(
      RoomEvent.DataReceived,
      (data, participant) => {
        // Two liveness rules share this handler (SSR-M4 integration of #1576
        // and #1674). Until this agent has spoken once, ANY room traffic arms
        // the silence timers so the proxy's cold-boot window can re-advertise
        // ($CTRL subscribe) on a Windows cold start where the encoder comes up
        // ~11s late. Once the agent has been heard, liveness is per-agent:
        // shared-room traffic from other identities must not re-arm this
        // connection's watchdog or cancel its probe, or a dead agent would be
        // kept alive by its neighbours' frames.
        const fromThisAgent = participant?.identity === this.#agentId;
        if (fromThisAgent) {
          this.#heardFromAgent = true;
        }
        if (fromThisAgent || !this.#heardFromAgent) {
          this.#resetInactiveTimeout();
        }
        this.#router.parseIncomingMessage(participant?.identity ?? '', data);
      },
      this.#agentId
    );

    // One reset+flush per reconnect. The previous 10×1s sleep loop amplified
    // recovery and used production sleeps.
    this.#livekit.onRoomEvent(
      RoomEvent.Reconnected,
      () => {
        void this.#flushAfterReconnect();
      },
      this.#agentId
    );

    this.#livekit.onRoomEvent(
      RoomEvent.ParticipantConnected,
      (participant) => {
        if (participant.identity === this.#agentId) {
          void this.#flushAfterReconnect();
        }
      },
      this.#agentId
    );

    this.#livekit.onRoomEvent(
      RoomEvent.ConnectionStateChanged,
      (state) => {
        if (state === 'connected') {
          void this.sendMsgBuffer();
        }
      },
      this.#agentId
    );

    if (this.isConnected()) {
      void this.sendMsgBuffer();
    }

    this.#router.subscribe(this.#agentId, '$timestamp', (msg) => {
      try {
        const payload = AgentDirectTrackTimestamp.decode(msg.data);
        SharedTimestampManager.setTimestampForAgent(this.#agentId, payload);
      } catch (e) {
        //console.error('Failure when receiving timestamp:', e, sendPromises);
      }
    });
  }

  async #flushAfterReconnect(): Promise<void> {
    if (this.#reconnectFlushInFlight) {
      return this.#reconnectFlushInFlight;
    }

    this.#reconnectFlushInFlight = (async () => {
      this.#router.reset(this.#agentId);
      await this.sendMsgBuffer();
    })();

    try {
      await this.#reconnectFlushInFlight;
    } finally {
      this.#reconnectFlushInFlight = undefined;
    }
  }

  async sendMsgBuffer(): Promise<void> {
    if (this.#sendBufferInFlight) {
      return this.#sendBufferInFlight;
    }

    this.#sendBufferInFlight = this.#drainMsgBuffer();

    try {
      await this.#sendBufferInFlight;
    } finally {
      this.#sendBufferInFlight = undefined;
    }
  }

  async #drainMsgBuffer(): Promise<void> {
    // Optional-chain publish resolves undefined and would wipe the queue.
    if (!this.#livekit) {
      return;
    }

    // Drop only after a successful publish so a closed PC cannot wipe
    // the offline queue. Per rule: settle-every-terminal-branch.
    // mode:auto — empty options used to fall through to a closed cloud PC.
    const pending = [...this.#msgBuffer];

    for (const msg of pending) {
      try {
        await this.#livekit.publishDataWithLocalParticipant(
          toIdFromHex(this.#agentId)!,
          { mode: 'auto' },
          msg,
          {
            destinationIdentities: [this.#agentId],
            reliable: true
          }
        );
        this.#msgBuffer.delete(msg);
        this.#msgBufferTotalBytes -= msg.length;
      } catch {
        // Keep for the next connected flush.
      }
    }
  }

  closed(): Promise<void | Error> {
    throw new Error('Method not implemented.');
  }

  close(): Promise<void> {
    // Ignore, newts doesn't manage the livekit connection
    return Promise.resolve();
  }

  publish(
    subject: string,
    data?: Uint8Array,
    options?: PublishOptions & {
      reject?: boolean;
      mode?: 'cloud' | 'local' | 'hybrid';
      lossy?: boolean;
    }
  ): void {
    const metaPart: DeepPartial<AgentDirectNewtsMessage> = {};

    if (options?.reply) {
      metaPart.replyTo = options.reply;
    }

    if (options?.reject) {
      if (!metaPart.control) {
        metaPart.control = {};
      }

      metaPart.control.reject = true;
    }

    const meta = AgentDirectNewtsMessage.fromPartial(metaPart);

    this.#router.publish(this.#agentId, subject, data ?? new Uint8Array([]), meta, options);
  }

  subscribe(subject: string, opts?: SubscriptionOptions): Subscription {
    const sub = this.#router.subscribe(this.#agentId, subject, (msg) => {
      opts?.callback?.(null, <Msg>(<unknown>msg));
    });

    return <Subscription>(<unknown>{
      unsubscribe: () => {
        this.#router.unsubscribe(this.#agentId, sub);
      },

      getSubject: () => {
        return subject;
      }
    });
  }

  async request(subject: string, data?: Uint8Array, opts?: RequestOptions): Promise<Msg> {
    if (data == null) {
      data = new Uint8Array(0);
    }

    try {
      const msg = await this.#router.request(
        this.#agentId,
        subject,
        data,
        opts?.timeout ?? 5000,
        opts?.reply
      );

      return <Msg>(<unknown>msg);
    } catch (e) {
      throw e;
    }
  }

  requestMany(
    _subject: string,
    _data: Uint8Array,
    _opts: Partial<RequestManyOptions>
  ): Promise<QueuedIterator<Msg>> {
    throw new Error('Method not implemented.');
  }

  flush(): Promise<void> {
    // Ignore, newts doesn't manage the livekit connection
    return Promise.resolve();
  }

  drain(): Promise<void> {
    // Ignore, newts doesn't manage the livekit connection
    return Promise.resolve();
  }

  isClosed(): boolean {
    if (!this.#livekit) {
      return true;
    }

    // S7: livekit.state() is OR over both rooms; disconnected only when neither is live.
    return this.#livekit.state(toIdFromHex(this.#agentId)!) === 'disconnected';
  }

  isDraining(): boolean {
    return false;
  }

  getServer(): string {
    return 'iris://';
  }

  status(): AsyncIterable<Status> {
    return <AsyncIterable<Status>>(<unknown>{});
  }

  stats(): Stats {
    return <Stats>{};
  }

  jetstreamManager(_opts?: JetStreamOptions): Promise<JetStreamManager> {
    throw new Error('Method not implemented.');
  }

  jetstream(_opts?: JetStreamOptions): JetStreamClient {
    throw new Error('Method not implemented.');
  }

  rtt(): Promise<number> {
    return Promise.resolve(-1);
  }

  isConnected() {
    if (!this.#livekit) {
      return false;
    }

    // S7: connected if either state transport is live.
    return this.#livekit.state(toIdFromHex(this.#agentId)!) === 'connected';
  }

  get connectedMode() {
    return this.#livekit?.connectedModeForAgent(toIdFromHex(this.#agentId)!);
  }

  get localModeCapable() {
    return this.#livekit?.localModeCapable;
  }

  services: ServicesAPI = <ServicesAPI>{};

  async inRoom(identity: string): Promise<boolean> {
    if (!this.#livekit) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return this.inRoom(identity);
    }

    return this.#livekit?.inRoom?.(toIdFromHex(this.#agentId)!, identity) ?? false;
  }

  inRoomUnsafe(identity: string): boolean {
    return this.#livekit?.inRoom?.(toIdFromHex(this.#agentId)!, identity) ?? false;
  }

  remoteParticipants(mode: 'cloud' | 'local' | 'hybrid'): Map<string, RemoteParticipant> {
    if (mode === 'cloud') {
      return (
        this.#livekit?.remoteParticipantsWithOptions(toIdFromHex(this.#agentId)!, {
          mode: 'cloud'
        }) ?? new Map()
      );
    }

    if (mode === 'local') {
      return (
        this.#livekit?.remoteParticipantsWithOptions(toIdFromHex(this.#agentId)!, {
          mode: 'local'
        }) ?? new Map()
      );
    }

    const local =
      this.#livekit?.remoteParticipantsWithOptions(toIdFromHex(this.#agentId)!, {
        mode: 'local'
      }) ?? new Map();
    const cloud =
      this.#livekit?.remoteParticipantsWithOptions(toIdFromHex(this.#agentId)!, {
        mode: 'cloud'
      }) ?? new Map();

    const result = new Map<string, RemoteParticipant>();

    for (const [identity, participant] of local) {
      result.set(identity, participant);
    }

    for (const [identity, participant] of cloud) {
      result.set(identity, participant);
    }

    return result;
  }

  #suspicionHandler: () => void = () => {};
  // Set on the first DataReceived carrying this agent's identity; gates the
  // per-agent watchdog rule above. Never cleared: the boot window closes once.
  #heardFromAgent = false;
  #inactiveTimeouts: NodeJS.Timeout[] = [];
  #restoreHandler: () => void = () => {};
  #connectionHealthy: boolean = false;
  #connectionAlive: boolean = false;

  setTimeoutHandler(_handler: (duration: number) => void): void {
    // Silence no longer posts UI from this timer; suspicionHandler is the
    // only fire-path callback. Setter kept so existing call sites compile.
  }

  setSuspicionHandler(handler: () => void): void {
    this.#suspicionHandler = handler;
  }

  setRestoreHandler(handler: () => void): void {
    this.#restoreHandler = handler;
  }

  isConnectionAlive(): boolean {
    return this.#connectionAlive;
  }

  // The depot onError path short-circuits on this flag. Only a completed
  // death verdict (probe cap-out or missing participant) may clear it —
  // inbound silence must not. Per rule: settle-every-terminal-branch
  // (ai.knowledge/global).
  noteConnectionDead(): void {
    this.#connectionAlive = false;
  }

  #resetInactiveTimeout(): void {
    for (const timeout of this.#inactiveTimeouts) {
      clearTimeout(timeout);
    }

    // Reset the router only on restore after a silence gap, never on the
    // fire path — a lull is not a disconnect.
    if (!this.#connectionHealthy) {
      this.#restoreHandler();
      this.#router.reset(this.#agentId);
    }

    this.#connectionAlive = true;
    this.#connectionHealthy = true;

    // 5s/10s windows are unchanged. Silence is suspicion, not death:
    // do not flip #connectionAlive or post UI from this timer.
    // Per rule: architecture-principles — control-plane liveness is not
    // media-room / inbound-cadence state.
    this.#inactiveTimeouts = [
      setTimeout(() => {
        this.#connectionHealthy = false;
        this.#suspicionHandler();
      }, 5_000),
      setTimeout(() => {
        this.#connectionHealthy = false;
        this.#suspicionHandler();
      }, 10_000)
    ];
  }

  async pingAgent(): Promise<boolean> {
    return (await this.#livekit?.pingAgent(toIdFromHex(this.#agentId)!)) ?? false;
  }

  async restartLocalConnection(): Promise<void> {
    return (await this.#livekit?.restartLocalConnection(toIdFromHex(this.#agentId)!)) ?? undefined;
  }

  async watchForDeviceIDOnLocal(deviceId: string): Promise<void> {
    return (
      (await this.#livekit?.watchForDeviceIDOnLocal(deviceId, toIdFromHex(this.#agentId)!)) ??
      undefined
    );
  }

  getLiveKitConnection(): T_Maybe<LiveKitImpl> {
    return this.#livekit;
  }

  resetRouter(): void {
    this.#router.reset(this.#agentId);
  }
}
